import { useQuery, useMutation } from '@apollo/client';
import { isNil } from '@sixfold/typed-primitives';
import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router';

import { telematicsIntegrationSensitiveDataQuery } from '../../company/graphql';
import {
  TelematicsIntegrationQuery,
  TelematicsIntegrationQueryVariables,
  UpdateTelematicsIntegrationMutation,
  UpdateTelematicsIntegrationMutationVariables,
  SensitiveDataQuery,
  SensitiveDataQueryVariables,
} from '../../lib/graphql';
import { Loader } from '../../lib/loader';
import { IntegrationView, IntegrationViewDataProps } from '../components/integration';
import { telematicsIntegrationQuery, updateTelematicsIntegrationMutation } from '../graphql';

class LoadingContainer extends Loader<
  TelematicsIntegrationQuery,
  IntegrationViewDataProps,
  TelematicsIntegrationQueryVariables
> {}
class DataLoadingContainer extends Loader<
  SensitiveDataQuery,
  {
    data: {
      sensitiveData:
        | {
            connectionData: Record<string, string | number | boolean | object> | null;
            syncStateData: string | null;
          }
        | null
        | undefined;
    };
  },
  SensitiveDataQueryVariables
> {}

export const IntegrationContainer = () => {
  const { telematics_integration_id, company_id } = useParams() as {
    company_id: string;
    telematics_integration_id: string;
  };
  const navigate = useNavigate();
  const [variables, setVariables] = useState<{ telematics_integration_id: string; captcha_token?: string }>({
    telematics_integration_id,
  });

  const isMounted = useRef(true);
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const addCaptchaToVariables = (captcha_token: string) => {
    if (isMounted.current) {
      setVariables({ telematics_integration_id, captcha_token });
    }
  };

  const result = useQuery<TelematicsIntegrationQuery, TelematicsIntegrationQueryVariables>(telematicsIntegrationQuery, {
    variables,
  });

  const [updateTelematicsIntegration] = useMutation<
    UpdateTelematicsIntegrationMutation,
    UpdateTelematicsIntegrationMutationVariables
  >(updateTelematicsIntegrationMutation);

  const captchaToken = variables.captcha_token;
  const sensitiveDataResult = useQuery<SensitiveDataQuery, SensitiveDataQueryVariables>(
    telematicsIntegrationSensitiveDataQuery,
    {
      variables: { ...variables, captcha_token: captchaToken ?? '' },
      skip: isNil(captchaToken),
    },
  );

  const getChildren = (data: IntegrationViewDataProps['data']): React.ReactNode => {
    if (captchaToken) {
      return (
        <DataLoadingContainer
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          result={sensitiveDataResult as any}
          mapData={({ data }) => ({
            data: {
              sensitiveData: data.sensitiveData,
            },
          })}>
          {({ data: connDataResult }) => (
            <IntegrationView
              data={data}
              sensitiveConnectionData={connDataResult.sensitiveData?.connectionData}
              sensitiveSyncStateData={connDataResult.sensitiveData?.syncStateData}
              updateIntegration={updateTelematicsIntegration}
              routeProps={{ push: (path: string) => navigate(path), goBack: () => navigate(-1) }}
              addCaptchaToVariables={addCaptchaToVariables}
            />
          )}
        </DataLoadingContainer>
      );
    }

    return (
      <IntegrationView
        data={data}
        updateIntegration={updateTelematicsIntegration}
        routeProps={{ push: (path: string) => navigate(path), goBack: () => navigate(-1) }}
        addCaptchaToVariables={addCaptchaToVariables}
      />
    );
  };

  return (
    <LoadingContainer
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      result={result as any}
      mapData={({ data }) => ({
        data: {
          company_id,
          telematicsIntegration: data.telematicsIntegration ?? null,
        },
      })}>
      {({ data }) => getChildren(data)}
    </LoadingContainer>
  );
};
